import { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { format, formatDistanceToNow } from 'date-fns';
import toast from 'react-hot-toast';
import { getParcels } from '../../api/parcels';
import { getChat, getChatMessages, initChat, sendMessage, uploadParcelImage } from '../../api/chat';
import { useParcelStore } from '../../store/parcelStore';
import { useChatStore } from '../../store/chatStore';
import Button from '../../components/common/Button';
import Spinner from '../../components/common/Spinner';

function ContextBadge({ label, timestamp }) {
  if (!timestamp) return null;
  const ago = formatDistanceToNow(new Date(timestamp), { addSuffix: true });
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-green-50 text-green-700 border border-green-200">
      {label} &middot; {ago}
    </span>
  );
}

function InitPanel({ parcel, onInitialized }) {
  const [imageFile, setImageFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const fileRef = useRef(null);

  const handleFile = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setImageFile(file);
    setPreview(URL.createObjectURL(file));
  };

  const handleAnalyze = async () => {
    if (!imageFile) { toast.error('Прво прикачете фотографија на полето.'); return; }
    setLoading(true);
    try {
      const form = new FormData();
      form.append('image', imageFile);
      const uploadRes = await uploadParcelImage(parcel.id, form);
      const initRes = await initChat(parcel.id, { image_url: uploadRes.data.image_url });
      onInitialized(initRes.data);
      toast.success('AI анализата е започната!');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Неуспешна иницијализација на AI анализата.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex-1 flex flex-col items-center justify-center p-8 text-center">
      <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
        <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
            d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
        </svg>
      </div>
      <h2 className="text-lg font-semibold text-gray-800 mb-1">Иницијализирај AI анализа</h2>
      <p className="text-sm text-gray-500 mb-6 max-w-sm">
        Прикачете фотографија на <strong>{parcel.parcel_name}</strong> за да добиете AI проценка на здравствената состојба на полето.
      </p>
      <div onClick={() => fileRef.current?.click()}
        className="w-full max-w-sm border-2 border-dashed border-gray-300 rounded-xl p-6 cursor-pointer hover:border-green-400 transition-colors mb-4">
        {preview ? (
          <img src={preview} alt="field preview" className="w-full h-40 object-cover rounded-lg" />
        ) : (
          <div className="flex flex-col items-center gap-2 text-gray-400">
            <svg className="w-10 h-10" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
                d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
            <span className="text-sm">Кликнете за да прикачите фотографија на полето</span>
          </div>
        )}
        <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={handleFile} />
      </div>
      <Button onClick={handleAnalyze} loading={loading} disabled={!imageFile || loading}>
        Анализирај поле
      </Button>
    </div>
  );
}

function parseMessageText(text) {
  try {
    const parsed = JSON.parse(text);
    return parsed.response ?? text;
  } catch {
    return text;
  }
}

function MessageBubble({ msg }) {
  const isUser = msg.sender_type === 'user';
  if (isUser && !msg.message_text && msg.image_url) {
    return (
      <div className="flex justify-end">
        <div className="max-w-[70%]">
          <img src={msg.image_url} alt="uploaded field" className="rounded-xl max-h-48 object-cover border border-gray-200" />
          <p className="text-xs text-gray-400 text-right mt-1">{format(new Date(msg.created_at), 'HH:mm')}</p>
        </div>
      </div>
    );
  }
  return (
    <div className={`flex ${isUser ? 'justify-end' : 'justify-start'}`}>
      {!isUser && (
        <div className="w-7 h-7 rounded-full bg-green-100 flex items-center justify-center mr-2 flex-shrink-0 mt-1">
          <span className="text-xs">🤖</span>
        </div>
      )}
      <div className={`max-w-[75%] px-4 py-2.5 rounded-2xl text-sm ${isUser ? 'bg-green-700 text-white rounded-br-sm' : 'bg-gray-100 text-gray-800 rounded-bl-sm'}`}>
        {msg.image_url && <img src={msg.image_url} alt="attachment" className="rounded-lg mb-2 max-h-36 object-cover" />}
        <p className="whitespace-pre-wrap">{isUser ? msg.message_text : parseMessageText(msg.message_text)}</p>
        <p className={`text-xs mt-1 ${isUser ? 'text-green-200' : 'text-gray-400'}`}>{format(new Date(msg.created_at), 'HH:mm')}</p>
      </div>
    </div>
  );
}

export default function Chat() {
  const [searchParams] = useSearchParams();
  const { parcels, setParcels } = useParcelStore();
  const { activeParcelId, setActiveParcel, sessions, setSession, messages, setMessages, addMessages } = useChatStore();

  const [loadingParcels, setLoadingParcels] = useState(parcels.length === 0);
  const [loadingSession, setLoadingSession] = useState(false);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [sending, setSending] = useState(false);
  const [search, setSearch] = useState('');
  const [input, setInput] = useState('');
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);

  const bottomRef = useRef(null);
  const fileRef = useRef(null);

  const activeParcel = parcels.find((p) => p.id === activeParcelId) || null;
  const activeSession = activeParcelId ? sessions[activeParcelId] : null;
  const activeMessages = useMemo(() => activeParcelId ? messages[activeParcelId] || [] : [], [activeParcelId, messages]);

  const filteredParcels = parcels.filter((p) => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return p.parcel_name?.toLowerCase().includes(q) || p.crop_type?.toLowerCase().includes(q);
  });

  useEffect(() => {
    if (parcels.length > 0) { setLoadingParcels(false); return; }
    getParcels()
      .then((res) => setParcels(res.data?.results ?? res.data))
      .catch(() => toast.error('Неуспешно вчитување на парцелите'))
      .finally(() => setLoadingParcels(false));
  }, [parcels.length, setParcels]);

  useEffect(() => {
    const pid = parseInt(searchParams.get('parcel'));
    if (pid && parcels.length > 0 && !activeParcelId) setActiveParcel(pid);
  }, [searchParams, parcels.length, activeParcelId, setActiveParcel]);

  const loadParcelChat = useCallback(async (parcelId) => {
    setLoadingSession(true);
    try {
      const sRes = await getChat(parcelId);
      setSession(parcelId, sRes.data);
    } catch { toast.error('Неуспешно вчитување на разговорот'); }
    finally { setLoadingSession(false); }

    setLoadingMessages(true);
    try {
      const mRes = await getChatMessages(parcelId);
      setMessages(parcelId, mRes.data?.results ?? mRes.data);
    } catch { toast.error('Неуспешно вчитување на пораките'); }
    finally { setLoadingMessages(false); }
  }, [setSession, setMessages]);

  useEffect(() => {
    if (!activeParcelId) return;
    loadParcelChat(activeParcelId);
  }, [activeParcelId, loadParcelChat]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [activeMessages]);

  const handleInitialized = (data) => {
    setSession(activeParcelId, { ...activeSession, initialized: true });
    addMessages(activeParcelId, [data.user_message, data.ai_message]);
  };

  const handleImageSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setImageFile(file);
    setImagePreview(URL.createObjectURL(file));
  };

  const handleSend = async (e) => {
    e.preventDefault();
    if (!input.trim() || !activeParcelId) return;
    const content = input.trim();
    setInput('');
    setSending(true);
    try {
      let imageUrl = null;
      if (imageFile) {
        const form = new FormData();
        form.append('image', imageFile);
        const uploadRes = await uploadParcelImage(activeParcelId, form);
        imageUrl = uploadRes.data.image_url;
        setImageFile(null);
        setImagePreview(null);
      }
      const payload = { message: content };
      if (imageUrl) payload.image_url = imageUrl;
      const res = await sendMessage(activeParcelId, payload);
      addMessages(activeParcelId, [res.data.user_message, res.data.ai_message]);
      const sRes = await getChat(activeParcelId);
      setSession(activeParcelId, sRes.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Неуспешно испраќање на пораката');
    } finally { setSending(false); }
  };

  return (
    <div className="flex h-[calc(100vh-7rem)] gap-4">
      {/* Странична лента */}
      <div className="w-72 flex-shrink-0 bg-white rounded-xl border border-gray-100 shadow-sm flex flex-col">
        <div className="p-3 border-b border-gray-100">
          <input type="text" value={search} onChange={(e) => setSearch(e.target.value)}
            placeholder="Пребарај парцели..."
            className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-500" />
        </div>
        <div className="flex-1 overflow-y-auto p-2 space-y-1">
          {loadingParcels ? (
            <div className="flex justify-center pt-6"><Spinner size="sm" /></div>
          ) : filteredParcels.length === 0 ? (
            <p className="text-xs text-gray-400 text-center pt-6">
              {parcels.length === 0 ? 'Сè уште нема парцели' : 'Нема парцели кои одговараат на пребарувањето'}
            </p>
          ) : filteredParcels.map((parcel) => {
            const session = sessions[parcel.id];
            const isActive = activeParcelId === parcel.id;
            return (
              <div key={parcel.id} onClick={() => setActiveParcel(parcel.id)}
                className={`px-3 py-2.5 rounded-lg cursor-pointer transition-colors ${isActive ? 'bg-green-50 border border-green-200' : 'hover:bg-gray-50'}`}>
                <div className="flex items-center justify-between mb-0.5">
                  <span className={`text-sm font-medium truncate ${isActive ? 'text-green-700' : 'text-gray-800'}`}>{parcel.parcel_name}</span>
                  {session && (
                    <span className={`text-xs px-1.5 py-0.5 rounded-full flex-shrink-0 ml-1 ${session.initialized ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                      {session.initialized ? 'Активен' : 'Нов'}
                    </span>
                  )}
                </div>
                <p className="text-xs text-gray-400 truncate">{parcel.crop_type || 'Нема поставена култура'}</p>
                {session?.last_message && (
                  <p className="text-xs text-gray-400 truncate mt-0.5">{parseMessageText(session.last_message.text) || '(слика)'}</p>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Главен панел */}
      <div className="flex-1 bg-white rounded-xl border border-gray-100 shadow-sm flex flex-col min-w-0">
        {!activeParcelId ? (
          <div className="flex-1 flex flex-col items-center justify-center text-gray-400">
            <span className="text-5xl mb-4">🌱</span>
            <p className="text-sm">Изберете парцела за да го отворите AI асистентот</p>
          </div>
        ) : loadingSession ? (
          <div className="flex-1 flex items-center justify-center"><Spinner /></div>
        ) : !activeSession?.initialized ? (
          <>
            <div className="px-5 py-4 border-b border-gray-100">
              <h2 className="font-semibold text-gray-800">{activeParcel?.parcel_name}</h2>
              <p className="text-xs text-gray-400">{activeParcel?.crop_type || 'Нема култура'}</p>
            </div>
            <InitPanel parcel={activeParcel} onInitialized={handleInitialized} />
          </>
        ) : (
          <>
            <div className="px-5 py-3 border-b border-gray-100">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="font-semibold text-gray-800">{activeParcel?.parcel_name}</h2>
                  <p className="text-xs text-gray-400">{activeParcel?.crop_type || 'Нема култура'} &middot; {activeParcel?.size_m2} м&sup2;</p>
                </div>
                <div className="flex gap-2 flex-wrap justify-end">
                  <ContextBadge label="Временска прогноза" timestamp={activeSession?.last_weather_sent_at} />
                  <ContextBadge label="Мерења" timestamp={activeSession?.last_measurements_sent_at} />
                </div>
              </div>
            </div>

            <div className="flex-1 overflow-y-auto p-5 space-y-4">
              {loadingMessages ? (
                <div className="flex justify-center pt-8"><Spinner /></div>
              ) : activeMessages.length === 0 ? (
                <p className="text-center text-gray-400 text-sm pt-8">Сè уште нема пораки.</p>
              ) : activeMessages.map((msg) => <MessageBubble key={msg.id} msg={msg} />)}
              {sending && (
                <div className="flex justify-start">
                  <div className="bg-gray-100 px-4 py-3 rounded-2xl rounded-bl-sm">
                    <span className="flex gap-1">
                      {[0, 1, 2].map((i) => (
                        <span key={i} className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"
                          style={{ animationDelay: `${i * 0.15}s` }} />
                      ))}
                    </span>
                  </div>
                </div>
              )}
              <div ref={bottomRef} />
            </div>

            {imagePreview && (
              <div className="px-5 py-2 border-t border-gray-50 flex items-center gap-3">
                <img src={imagePreview} alt="attachment" className="h-14 w-14 rounded-lg object-cover" />
                <button onClick={() => { setImageFile(null); setImagePreview(null); }}
                  className="text-xs text-red-400 hover:text-red-600">Отстрани</button>
              </div>
            )}

            <form onSubmit={handleSend} className="px-5 py-4 border-t border-gray-100 flex gap-2">
              <button type="button" onClick={() => fileRef.current?.click()}
                className="flex-shrink-0 p-2 text-gray-400 hover:text-green-600 rounded-lg hover:bg-gray-100 transition-colors" title="Прикачи слика">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                    d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
              </button>
              <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={handleImageSelect} />
              <input type="text" value={input} onChange={(e) => setInput(e.target.value)}
                placeholder="Прашајте за вашето поле..."
                className="flex-1 border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-green-500" />
              <Button type="submit" disabled={!input.trim() || sending}>Испрати</Button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
